<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { debounce } from 'lodash'
import mapboxgl from 'mapbox-gl'

const props = defineProps({
  map: { type: Object, required: true },
  activeTab: { type: String, required: true },
})

const router = useRouter()
const route = useRoute()
const store = useStore()

const SOURCE_ID = 'project-boundaries'
const FILL_LAYER = 'boundaries-fill'
const OUTLINE_LAYER = 'boundaries-outline'
const LABEL_LAYER = 'boundaries-label'
const popup = ref(null)
let hoveredId = null

const boundaries = computed(() => store.getters['boundary/getBoundaries'])

const currentProjectId = computed(() => {
  const id = parseInt(route.params.project_id)
  return isNaN(id) ? null : id
})

const flatedProjects = computed(() => store.getters['project/getFlatedProjects'])

const visibleBoundaries = computed(() => {
  if (!boundaries.value || !boundaries.value.features) {
    return { type: 'FeatureCollection', features: [] }
  }

  let features = boundaries.value.features.filter(f => f.geometry !== null)

  if (!currentProjectId.value) {
    // Org dashboard: show top-level boundaries only
    features = features.filter(f => f.properties.parent_id === null)
  } else {
    // Viewing a project: show sibling/child boundaries (not the current project itself)
    const currentProject = flatedProjects.value?.find(p => p.project_id === currentProjectId.value)
    if (currentProject) {
      const hasChildren = flatedProjects.value?.some(p => p.parent_id === currentProjectId.value)
      if (hasChildren) {
        // Parent project: show children
        features = features.filter(f => f.properties.parent_id === currentProjectId.value)
      } else {
        // Leaf project (no children): show nothing
        features = []
      }
    }
  }

  return { type: 'FeatureCollection', features }
})

const fetchBounds = async () => {
  if (!props.map || props.activeTab !== 'projects') return

  const bounds = props.map.getBounds()
  if (!bounds) return

  await store.dispatch('boundary/fetchBoundaries', {
    west: bounds.getWest(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    north: bounds.getNorth(),
    zoom: Math.round(props.map.getZoom()),
  })
}

const debouncedFetchBounds = debounce(fetchBounds, 400)

// Render emoji to a canvas image for use as a Mapbox icon
const emojiImageCache = new Set()

const ensureEmojiImage = (emoji) => {
  if (!emoji || emojiImageCache.has(emoji)) return
  emojiImageCache.add(emoji)

  const size = 32
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  ctx.font = `${size - 4}px serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(emoji, size / 2, size / 2 + 1)

  const imageId = `emoji-${emoji}`
  if (!props.map.hasImage(imageId)) {
    props.map.addImage(imageId, ctx.getImageData(0, 0, size, size), {
      pixelRatio: 2,
    })
  }
}

const syncEmojiImages = () => {
  if (!visibleBoundaries.value?.features) return
  for (const f of visibleBoundaries.value.features) {
    ensureEmojiImage(f.properties.emoji)
  }
}

const addLayers = () => {
  if (!props.map) return

  // Add source
  if (!props.map.getSource(SOURCE_ID)) {
    props.map.addSource(SOURCE_ID, {
      type: 'geojson',
      data: visibleBoundaries.value,
      promoteId: 'project_id',
    })
  }

  // Fill layer
  if (!props.map.getLayer(FILL_LAYER)) {
    props.map.addLayer({
      id: FILL_LAYER,
      type: 'fill',
      source: SOURCE_ID,
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], '#0039A3'],
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          0.35,
          0.12,
        ],
      },
    })
  }

  // Outline layer
  if (!props.map.getLayer(OUTLINE_LAYER)) {
    props.map.addLayer({
      id: OUTLINE_LAYER,
      type: 'line',
      source: SOURCE_ID,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#0039A3'],
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          4,
          2,
        ],
        'line-dasharray': [4, 2],
      },
    })
  }

  // Sync emoji images before adding the label layer
  syncEmojiImages()

  // Label layer
  if (!props.map.getLayer(LABEL_LAYER)) {
    props.map.addLayer({
      id: LABEL_LAYER,
      type: 'symbol',
      source: SOURCE_ID,
      layout: {
        'icon-image': [
          'case',
          ['>', ['length', ['coalesce', ['get', 'emoji'], '']], 0],
          ['concat', 'emoji-', ['get', 'emoji']],
          ''
        ],
        'icon-size': 1,
        'icon-anchor': 'center',
        'icon-text-fit': 'none',
        'icon-optional': true,
        'text-field': ['get', 'project_name'],
        'text-size': 14,
        'text-font': ['Open Sans Bold'],
        'text-allow-overlap': false,
        'text-offset': [0, 0],
        'icon-offset': [0, -12],
      },
      paint: {
        'text-color': '#fff',
        'text-halo-color': ['coalesce', ['get', 'color'], '#0039A3'],
        'text-halo-width': 1.5,
      },
    })
  }
}

const removeLayers = () => {
  if (!props.map) return
  ;[LABEL_LAYER, OUTLINE_LAYER, FILL_LAYER].forEach(id => {
    if (props.map.getLayer(id)) props.map.removeLayer(id)
  })
  if (props.map.getSource(SOURCE_ID)) props.map.removeSource(SOURCE_ID)
}

const updateSource = () => {
  syncEmojiImages()
  const source = props.map?.getSource(SOURCE_ID)
  if (source) {
    source.setData(visibleBoundaries.value)
  }
}

// Hover handlers
const buildPopupContent = (properties) => {
  const emoji = properties.emoji || ''
  const name = properties.project_name || ''
  const placeDetails = typeof properties.place_details === 'string'
    ? JSON.parse(properties.place_details)
    : properties.place_details

  const container = document.createElement('div')
  container.style.cssText = 'padding:4px 6px;min-width:120px'

  const title = document.createElement('div')
  title.style.cssText = 'font-weight:600;font-size:13px'
  title.textContent = emoji ? `${emoji} ${name}` : name
  container.appendChild(title)

  if (placeDetails?.localname) {
    const subtitle = document.createElement('div')
    subtitle.style.cssText = 'font-size:11px;color:#666;margin-top:2px'
    subtitle.textContent = placeDetails.localname
    container.appendChild(subtitle)
  }

  const hint = document.createElement('div')
  hint.style.cssText = 'font-size:10px;color:#999;margin-top:3px'
  hint.textContent = 'Click to open'
  container.appendChild(hint)

  return container
}

const onMouseMove = (e) => {
  if (!e.features?.length) return

  const feature = e.features[0]
  const projectId = feature.properties.project_id

  if (hoveredId !== null && hoveredId !== projectId) {
    props.map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false })
  }

  hoveredId = projectId
  props.map.setFeatureState({ source: SOURCE_ID, id: projectId }, { hover: true })
  props.map.getCanvas().style.cursor = 'pointer'
  store.commit('boundary/SET_HOVERED_BOUNDARY_PROJECT_ID', projectId)

  // Show popup
  if (!popup.value) {
    popup.value = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10,
    })
  }
  popup.value
    .setLngLat(e.lngLat)
    .setDOMContent(buildPopupContent(feature.properties))
    .addTo(props.map)
}

const onMouseLeave = () => {
  if (hoveredId !== null) {
    props.map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false })
    hoveredId = null
  }
  props.map.getCanvas().style.cursor = ''
  store.commit('boundary/SET_HOVERED_BOUNDARY_PROJECT_ID', null)
  if (popup.value) {
    popup.value.remove()
  }
}

const onClick = (e) => {
  if (!e.features?.length) return
  const feature = e.features[0]
  const orgId = route.params.org_id
  const projectId = feature.properties.project_id
  if (orgId && projectId) {
    router.push({ name: 'ProjectHome', params: { org_id: orgId, project_id: projectId } })
  }
}

const addEventListeners = () => {
  props.map.on('mousemove', FILL_LAYER, onMouseMove)
  props.map.on('mouseleave', FILL_LAYER, onMouseLeave)
  props.map.on('click', FILL_LAYER, onClick)
  props.map.on('dragend', debouncedFetchBounds)
  props.map.on('zoomend', debouncedFetchBounds)
}

const removeEventListeners = () => {
  props.map.off('mousemove', FILL_LAYER, onMouseMove)
  props.map.off('mouseleave', FILL_LAYER, onMouseLeave)
  props.map.off('click', FILL_LAYER, onClick)
  props.map.off('dragend', debouncedFetchBounds)
  props.map.off('zoomend', debouncedFetchBounds)
}

// Watch for data changes
watch(visibleBoundaries, () => {
  updateSource()
})

watch(() => props.activeTab, (newTab) => {
  if (newTab === 'projects') {
    addLayers()
    fetchBounds()
  } else {
    removeLayers()
  }
})

watch(currentProjectId, () => {
  if (props.activeTab === 'projects') {
    fetchBounds()
  }
})

// Re-fetch when route name changes (e.g. ProjectCreate → ProjectHome with same project_id)
watch(() => route.name, () => {
  if (props.activeTab === 'projects') {
    debouncedFetchBounds()
  }
})

onMounted(async () => {
  if (props.activeTab === 'projects') {
    await fetchBounds()
    addLayers()
    addEventListeners()
  }
})

onUnmounted(() => {
  removeEventListeners()
  removeLayers()
  if (popup.value) {
    popup.value.remove()
    popup.value = null
  }
})
</script>

<template>
  <!-- Renderless component — all rendering done via Mapbox GL -->
</template>
